import { useTranslation } from 'react-i18next';
import { getDynamicTranslationForArray } from '@monorepo/client-common';
import { Stack, Typography, useTheme } from '@monorepo/nova-ui';
import { MenuChildLink, MenuLink } from '@monorepo/types';
import { useMenuTranslationsQuery } from '../../../api/queries/use-menu-translations-query';
import { StyledFooterLinkButton } from './styles/footer-link-button.styled';
import { StyledFooterLinkText } from './styles/footer-link-text.styled';

interface FooterColumnProps {
  title: string;
  links: MenuChildLink[];
  sx?: object;
}

const isAbsoluteUrl = (href: string): boolean => {
  return href.startsWith('http://') || href.startsWith('https://');
};

const FooterColumn = ({ title, links, sx }: FooterColumnProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: menuTranslations } = useMenuTranslationsQuery();
  const i18nEntries = menuTranslations?.['footerConfig.links'] || [];

  return (
    <Stack
      sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: { xs: 2.5 },
        ...sx,
      }}
    >
      <Typography
        variant="body2"
        fontWeight={theme.typography.fontWeightBold}
        fontSize={theme.typography.pxToRem(14)}
        letterSpacing={theme.typography.pxToRem(0.07)}
        color={theme.palette.text.main}
      >
        {title}
      </Typography>
      {links
        ?.filter((link) => link && link.active && link.label && link.href)
        ?.map((link, index) => {
          if (!link?.label || !link?.href) {
            return null;
          }

          const isAbsolute = isAbsoluteUrl(link.href);

          const translatedLabel = getDynamicTranslationForArray(
            link.label,
            i18nEntries,
            link.id,
            'label',
            t,
          );

          return (
            <StyledFooterLinkButton
              key={link.id || index}
              href={link.href}
              {...(isAbsolute && {
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
              aria-label={translatedLabel}
            >
              <StyledFooterLinkText variant="body2">
                {translatedLabel}
              </StyledFooterLinkText>
            </StyledFooterLinkButton>
          );
        })
        .filter(Boolean)}
    </Stack>
  );
};

export const RenderFooterColumns = ({
  footerLinks,
}: {
  footerLinks: MenuLink[];
}) => {
  const { t } = useTranslation();
  const { data: menuTranslations } = useMenuTranslationsQuery();
  const i18nEntries = menuTranslations?.['footerConfig.links'] || [];

  if (!footerLinks || !Array.isArray(footerLinks) || footerLinks.length === 0) {
    return null;
  }

  const [resourcesColumn, legalColumn, aboutColumn] = footerLinks;

  const getTranslatedTitle = (column: MenuLink | undefined): string => {
    if (!column) return '';
    return getDynamicTranslationForArray(
      column.label,
      i18nEntries,
      column.id,
      'label',
      t,
    );
  };

  const columnsToRender = [
    resourcesColumn?.label && resourcesColumn ? (
      <FooterColumn
        key="resources"
        title={getTranslatedTitle(resourcesColumn)}
        links={resourcesColumn.children || []}
        sx={{ order: { xs: 1, sm: 1 } }} // Resources - first
      />
    ) : null,
    legalColumn?.label && legalColumn ? (
      <FooterColumn
        key="legal"
        title={getTranslatedTitle(legalColumn)}
        links={legalColumn.children || []}
        sx={{
          order: { xs: 3, sm: 2 },
          gridColumnStart: { xs: 'span 2', sm: 'auto' },
        }} // Legal - second on mobile, third on desktop
      />
    ) : null,
    aboutColumn?.label && aboutColumn ? (
      <FooterColumn
        key="about"
        title={getTranslatedTitle(aboutColumn)}
        links={aboutColumn.children || []}
        sx={{ order: { xs: 2, sm: 3 } }} // About - third on mobile, second on desktop
      />
    ) : null,
  ].filter(Boolean); // Remove null entries

  if (columnsToRender.length === 0) {
    return null;
  }

  return (
    <Stack
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr 1fr',
          sm: `repeat(${columnsToRender.length}, 1fr)`,
          md: 'repeat(3, 1fr)',
        },
        rowGap: 6,
        columnGap: 0,
        mt: { xs: 8, md: 0 },
      }}
    >
      {columnsToRender}
    </Stack>
  );
};
